import { useState, useEffect } from 'react';
import { matchPath, useLocation } from 'react-router';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { CollapsibleContent } from 'ui/collapsibleContent';
import { colors, spacing } from 'ui/theme';
import { pxToRem } from 'utils/convert';
import { SideNavLinkList } from '../SideNavLinkList';
import { SideNavSectionConfig } from '../types';
import { getSideNavAnchorTagStyles, isSection } from '../utils';

const Wrapper = styled.li`
  min-width: ${spacing.x12};
  margin-right: ${spacing.base};
`;

const Header = styled.div<{ isActive: boolean; isExpanded: boolean }>`
  color: ${colors.textSecondary};
  margin-bottom: ${({ isExpanded }) => isExpanded && spacing.half};
  display: flex;
  align-items: center;

  & a {
    ${({ isActive }) => getSideNavAnchorTagStyles(isActive)}
  }

  && svg path {
    stroke: ${({ isActive }) => (isActive ? colors.brandPurple : colors.textSecondary)};
    stroke-width: ${pxToRem(3)};
  }
`;

const Container = styled.ul`
  padding: 0 0 0 ${spacing.base};
  margin: 0;
  overflow: auto;
  display: flex;
  flex-direction: column;
  row-gap: ${spacing.half};
`;

export const NestedSideNavSection = ({ header, links }: SideNavSectionConfig) => {
  const location = useLocation();
  const linkMatchesWithinSection = links.map((link) => {
    if (!isSection(link)) {
      return matchPath(location.pathname, {
        path: link.pathname,
        exact: link.exact,
      });
    }
    return null;
  });
  const doesSectionContainActiveLink = linkMatchesWithinSection.some((match) => !!match);
  const firstLinkInSectionIsActive = !!linkMatchesWithinSection[0];
  const [isExpanded, setIsExpanded] = useState(doesSectionContainActiveLink);
  const linkTo = !isSection(links[0])
    ? { search: links[0].search, pathname: links[0].pathname }
    : {};

  useEffect(() => {
    setIsExpanded(doesSectionContainActiveLink);
  }, [doesSectionContainActiveLink]);

  return (
    <Wrapper data-test={`nested-side-nav-${isExpanded ? 'expanded' : 'collapsed'}-section`}>
      <Header
        data-test="nested-side-nav-header-link"
        isActive={doesSectionContainActiveLink}
        isExpanded={isExpanded}
      >
        <Link
          data-test={`nested-side-nav-header-link-${header}`}
          to={linkTo}
          onClick={() => {
            if (firstLinkInSectionIsActive) {
              setIsExpanded(!isExpanded);
            }
          }}
        >
          {header}
        </Link>
      </Header>
      <CollapsibleContent isCollapsed={!isExpanded}>
        <Container>
          <SideNavLinkList linksOrSection={links} isSubLink />
        </Container>
      </CollapsibleContent>
    </Wrapper>
  );
};
